import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import TablePagination from '@mui/material/TablePagination';
import TableContainer from '@mui/material/TableContainer';
import CircularProgress from '@mui/material/CircularProgress';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import Menu from '@mui/material/Menu';
import Box from '@mui/material/Box';
import FilterListIcon from '@mui/icons-material/FilterList';
import Close from '@mui/icons-material/Close';
import { darken } from '@mui/system';
import styled from '@emotion/styled';

import { usePWAInstall } from '../../../hooks/usePWAInstall';
import { useDataAuth } from '../../../hooks/useDataAuth';

import { getDetails, getColumns, LoadData } from '../../../redux/actions/sharedViews';

import { FilterTable } from '../../../components/others/FilterTable';
import { LoadButtonHeaderTable } from '../../../components/sharedViews/loadActionsHeader/LoadButtonHeaderTable';
import { LoadButtons } from './LoadButtons';
import { ActionIconItem } from '../../../components/others/ActionIconItem';
import { formatNumber } from '../../../utils/invoice';
import { TruncateText } from '../../../components/others/TruncateText';

import identificationTypes from "../../../data/identificationTypes.json";
import { getNestedValue } from '../../../utils/objectHandlers';
import { Checkbox } from '@mui/material';
import { CheckCircle, RadioButtonUnchecked } from '@mui/icons-material';
import { actionTypes } from '../../../redux/contants/actionTypes';


const MuiTableHead = styled(TableHead)(({ theme }) => ({
    '& .css-x6proc-MuiTableCell-root': {
        backgroundColor: '#b4b4b4',
        borderRight: `thin dotted   ${darken(theme.palette.text.main, 0.8)}`,
    },
    '& .MuiTableCell-root': {
        borderRight: `thin dotted   ${darken(theme.palette.text.main, 0.8)}`,
    },
    color: theme.palette.primary.main,
    '& .MuiTableCell-root span': {
        fontWeight: 'bold',
        '&.Mui-inactive, .MuiTableSortLabel-icon ': {
            color: theme.palette.text.main,
            opacity: 0.2,
        },
        '&:hover': {
            color: theme.palette.primary.main,
        }
    },
    '& .MuiTableCell-root span.MuiSvgIcon-root:hover': {
        color: theme.palette.text.main,
    }
}));

const EMPTY = () => {
    const { t } = useTranslation();
    return <Typography textAlign="center" py={6}>{t('messages.noRecords')}</Typography>
};

const LOADING = () => (
    <Box sx={{ textAlign: 'center', py: 6 }}>
        <CircularProgress />
    </Box>
);

export const PopUpSharedTable = ({ item, data, DocManager, handleChange = () => { } }) => {
    const { t } = useTranslation();
    const { isMobileScreen } = usePWAInstall();
    const [{ userSession }] = useDataAuth();
    const dispatch = useDispatch();

    const [open, setOpen] = useState(false);
    const [idUserAction, setIdUserAction] = useState('');
    const [expandedId, setExpandedId] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    let [conf, setConf] = useState({});
    const [pagination] = useState({ pageNumber: 1, pageSize: 25 });
    const [filters] = useState(() => {
        if (!item.config?.filters) return {};

        return Object.fromEntries(
            Object.entries(item.config.filters).map(([key, filter]) => {
                const hasValue = filter.hasOwnProperty('value');
                const match = hasValue && typeof filter.value === 'string'
                    ? filter.value.match(/^{(.*)}$/)
                    : null;

                if (match) {
                    const variable = match[1];
                    return [key, { ...filter, value: data[variable] }];
                }
                return [key, filter];
            })
        );
    });

    const {
        listDeatils,
        listConfiguration,
        columsTableDeatails,
        isLoadingDetails,
        table_check,
        rowsCheckedDetails
    } = DocManager;

    const GetDetails = useCallback((id, variables) => {
        dispatch(getDetails(userSession.token, id, variables));
    }, [dispatch, userSession.token]);

    const handleChangePage = (event, newPage) => {
        pagination['pageNumber'] = newPage + 1;
        GetDetails(idUserAction, { filters, pagination });
    };

    const handleChangeRowsPerPage = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        pagination['pageNumber'] = 1;
        pagination['pageSize'] = newRowsPerPage;
        GetDetails(idUserAction, { filters, pagination });
    };

    const Search = (param, param2) => {
        filters[param2] = param;
        pagination['pageNumber'] = 1;
        GetDetails(idUserAction, { filters, pagination });
    }

    const ResetSearch = (param) => {
        delete filters[param];
        GetDetails(idUserAction, { filters, pagination });
    }

    const Refresh = () => GetDetails(idUserAction, { filters, pagination });

    const GetColumns = (config) => {
        setConf(config);
        setIdUserAction(config.id_user_action);
        dispatch(getColumns(userSession.token, config.id_user_action));
        GetDetails(config.id_user_action, { filters, pagination });
        setOpen(true);
    }

    //#region Check Handlers
    // const [rowsCheckedDetails, setrowsCheckedDetails] = useState([]);
    const isCheck = (id) => rowsCheckedDetails.includes(id);
    const handleRowsCheckedDetails = (obj) => dispatch(LoadData(actionTypes.rowsCheckedDetails, obj));
    const handleCheckAll = ({ target: { checked } }) => handleRowsCheckedDetails(checked ? listDeatils.map((item) => item.id) : []);

    const handleCheck = (id) => {
        const isSelected = rowsCheckedDetails.includes(id);
        if (isSelected) {
            handleRowsCheckedDetails(rowsCheckedDetails.filter(row => row !== id));
            if (expandedId === id) {
                setExpandedId(null);
            }
        } else {
            handleRowsCheckedDetails([...rowsCheckedDetails, id]);
            setExpandedId(prev => prev === id ? null : id);
        }
    };

    const handleClose = () => {
        dispatch(LoadData(actionTypes.rowsCheckedDetails, []));
        dispatch(LoadData(actionTypes.checkInTable, false));
        setOpen(false);
        handleChange();
    }
    //#end region Check Handlers

    return (
        <React.Fragment>
            <ActionIconItem
                item={item}
                onClick={() => GetColumns(item?.config)}
                defaultIcon="Task"
                badgeContent={item?.badge ? (data?.[item.badge] ?? null) : null}
            />

            <Dialog
                open={open}
                fullWidth
                maxWidth='Large'
                PaperProps={{
                    style: {
                        height: '93%',
                        background: '#e7e7f2',
                        px: '16px',
                    },
                }}
            >
                <DialogTitle
                    sx={{ pt: '5px', pb: '5px' }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Box
                            sx={{
                                justifyContent: 'flex-start',
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            <Typography sx={{
                                color: (theme) => theme.palette.text.primary,
                                fontWeight: 'bold',
                                fontSize: isMobileScreen ? "17px" : "20px"
                            }}>
                                {'Consultar documentos'}
                            </Typography>
                        </Box>
                        <Box
                            className="box-actions-header"
                            sx={{
                                justifyContent: 'flex-end',
                                flexWrap: isMobileScreen ? 'wrap' : 'nowrap',
                                width: isMobileScreen ? '100%' : 'auto'
                            }}
                        >
                            {isMobileScreen && (
                                <>
                                    <IconButton
                                        color="primary"
                                        onClick={(e) => setAnchorEl(e.currentTarget)}
                                        aria-label="abrir filtros"
                                    >
                                        <FilterListIcon />
                                    </IconButton>

                                    <Menu
                                        anchorEl={anchorEl}
                                        open={Boolean(anchorEl)}
                                        onClose={() => setAnchorEl(null)}
                                        PaperProps={{ sx: { minWidth: 220, p: 1 } }}
                                    >
                                        <Typography
                                            variant="subtitle2"
                                            sx={{ px: 2, pb: 1, fontWeight: 'bold', color: 'text.secondary' }}
                                        >
                                            Filtros disponibles
                                        </Typography>

                                        {columsTableDeatails.filter(col => col.filterable).map((col, idx) => (
                                            <MenuItem
                                                key={idx}
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'row',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    gap: 2
                                                }}
                                            >
                                                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                                                    {col.label}
                                                </Typography>
                                                <FilterTable
                                                    colum={col}
                                                    Search={Search}
                                                    ResetSearch={ResetSearch}
                                                    filter={filters.hasOwnProperty(col.id)}
                                                />
                                            </MenuItem>
                                        ))}
                                    </Menu>
                                </>
                            )}
                            <LoadButtonHeaderTable
                                buttons={listConfiguration}
                                id_user_action={conf.id_user_action}
                                parent={data}
                                DocManager={DocManager}
                                columns={columsTableDeatails}
                                handleChange={Refresh}
                                data={data}
                            />
                            <IconButton
                                aria-label='close'
                                onClick={handleClose}
                                sx={{ color: (theme) => theme.palette.text.main }}
                            >
                                <Close />
                            </IconButton>
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {/* MOBILE VIEW */}
                    {isMobileScreen ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {isLoadingDetails ? (<LOADING />) :
                                listDeatils.length === 0 ? (<EMPTY />) :
                                    (
                                        listDeatils.map((item) => (
                                            <Paper key={item.id} className="paper-item" onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}>
                                                <Box className="item-flex-container" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Box sx={{ minWidth: 0 }}>
                                                        {expandedId === item.id ?
                                                            (columsTableDeatails.filter(col => col.visible).map((col, idx) =>
                                                            (<Box key={idx} className="expanded-field">
                                                                <Typography variant="caption" className="field-label">
                                                                    {col.label}:
                                                                </Typography>
                                                                <Typography variant="body2" className="field-value">
                                                                    {getNestedValue(item, col.id)}
                                                                </Typography>
                                                            </Box>))
                                                            ) : (
                                                                columsTableDeatails.filter(col => col.visible && (col.filterable || col.showMobile))
                                                                    .map((col, idx) => (
                                                                        <Typography key={idx} variant="body2" className="text-ellipsis-dots">
                                                                            {getNestedValue(item, col.id)}
                                                                        </Typography>
                                                                    ))
                                                            )}
                                                    </Box>
                                                </Box>
                                                <Box
                                                    sx={{
                                                        ml: 'auto',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        alignSelf: 'center',
                                                    }}
                                                >
                                                    <LoadButtons data={item} handleChange={Refresh} DocManager={DocManager} isMobileScreen={isMobileScreen} />
                                                </Box>
                                            </Paper>
                                        ))
                                    )}
                        </Box>
                    ) : (
                        <Box className='custom-hard-table-zd-nix' sx={{ width: '100%' }}>
                            <Paper
                                sx={{ width: '100%' }}
                                elevation={3}
                            >
                                <TableContainer
                                    style={{ height: '65vh', overflow: 'auto', borderBottom: 'solid 1px' }}
                                >
                                    <Table
                                        aria-labelledby='tableTitle'
                                        size='small'
                                        stickyHeader
                                    >
                                        <MuiTableHead>
                                            <TableRow sx={{ px: 2 }}>
                                                {table_check &&
                                                    <TableCell
                                                        sx={{
                                                            minWidth: 40,
                                                            position: 'sticky',
                                                            zIndex: 1000,
                                                            left: 0
                                                        }}
                                                        id='TcellCheck'
                                                    >
                                                        <Checkbox
                                                            checked={listDeatils.length > 0 && rowsCheckedDetails.length === listDeatils.length}
                                                            onChange={handleCheckAll}
                                                            icon={<RadioButtonUnchecked fontSize="medium" />}
                                                            checkedIcon={<CheckCircle fontSize="medium" />}
                                                            color="primary"
                                                            sx={{
                                                                "&.Mui-checked": {
                                                                    color: 'black',
                                                                    transform: "scale(1.1)",
                                                                    transition: "transform 0.2s ease",
                                                                },
                                                            }}
                                                        />
                                                    </TableCell>
                                                }
                                                <TableCell
                                                    padding='normal'
                                                    align='center'
                                                    style={{ minWidth: 120, borderTopLeftRadius: !table_check && '14px' }}
                                                >
                                                    <Typography sx={{ fontWeight: 'bold' }} component='span'>
                                                        {t('actions')}
                                                    </Typography>
                                                </TableCell>
                                                {
                                                    (columsTableDeatails && columsTableDeatails.length) > 0 &&
                                                    columsTableDeatails.map((col, index) => (
                                                        col.visible &&
                                                        <TableCell
                                                            padding='normal'
                                                            align='center'
                                                            key={`tc-${index}`}
                                                            sx={{
                                                                width: col?.width ? `${col?.width}px` : "auto",
                                                                minWidth: col?.width ? `${col?.width}px` : "auto",
                                                                maxWidth: col?.width ? `${col?.width}px` : "auto",
                                                            }}
                                                        >
                                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                <Typography sx={{ fontWeight: 'bold', marginRight: '8px' }} component='span'>
                                                                    {col.label}
                                                                </Typography>
                                                                {col.filterable && (
                                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                        <FilterTable colum={col} Search={Search} ResetSearch={ResetSearch} filter={filters.hasOwnProperty(col.id)} />
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        </TableCell>
                                                    ))
                                                }
                                            </TableRow>
                                        </MuiTableHead>

                                        <TableBody>
                                            {
                                                isLoadingDetails ? (
                                                    <TableRow sx={{ height: 200, py: 2 }}>
                                                        <TableCell
                                                            colSpan={columsTableDeatails ? columsTableDeatails.length + 2 : 2}
                                                            align='center'
                                                        >
                                                            <LOADING />
                                                        </TableCell>
                                                    </TableRow>
                                                ) : !isLoadingDetails && !listDeatils.length > 0 ? (
                                                    <TableRow sx={{ height: 200, py: 2 }}>
                                                        <TableCell
                                                            colSpan={columsTableDeatails ? columsTableDeatails.length + 2 : 2}
                                                            align='center'
                                                        >
                                                            <EMPTY />
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    listDeatils.map((item) => (
                                                        <TableRow
                                                            tabIndex={-1}
                                                            key={item.id}
                                                        >
                                                            {table_check &&
                                                                <TableCell
                                                                    style={{ position: 'sticky', left: 0, backgroundColor: 'white' }}
                                                                >
                                                                    <Checkbox
                                                                        key={item.id}
                                                                        checked={isCheck(item.id)}
                                                                        onChange={() => handleCheck(item.id)}
                                                                        icon={<RadioButtonUnchecked fontSize="medium" />}
                                                                        checkedIcon={<CheckCircle fontSize="medium" />}
                                                                        color="primary"
                                                                        className="customCheckbox"
                                                                        sx={{ justifyContent: "center" }}
                                                                    />
                                                                </TableCell>
                                                            }
                                                            <TableCell padding='normal' align='center'>
                                                                <Typography>
                                                                    <LoadButtons data={item} handleChange={Refresh} DocManager={DocManager} isMobileScreen={false} />
                                                                </Typography>
                                                            </TableCell>
                                                            {
                                                                columsTableDeatails
                                                                    .filter(col => col.visible)
                                                                    .map((col, index) => {
                                                                        let content = getNestedValue(item, col.id);;
                                                                        if (col.type === 'money')
                                                                            content = formatNumber(content);
                                                                        if (col.id === 'type_identification')
                                                                            content = identificationTypes.find(type => type.value === content)?.label || 'Desconocido';

                                                                        return (
                                                                            <TableCell key={`tc-${index}`} padding='normal' align={col?.align || 'center'}>
                                                                                {col?.truncate ?
                                                                                    <TruncateText
                                                                                        text={content || ''}
                                                                                        limit={col.truncate}
                                                                                    />
                                                                                    :
                                                                                    <Typography>{content}</Typography>
                                                                                }
                                                                            </TableCell>
                                                                        );
                                                                    })
                                                            }
                                                        </TableRow>
                                                    ))
                                                )
                                            }
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <TablePagination
                                    sx={{
                                        "& .MuiIconButton-root": {
                                            color: "#6728AF",
                                        },
                                        "& .MuiIconButton-root.Mui-disabled": {
                                            color: "rgba(0, 0, 0, 0.26)",
                                        },
                                        ".css-mmygx2-MuiSelect-select-MuiInputBase-input.css-mmygx2-MuiSelect-select-MuiInputBase-input": {
                                            color: "#6728AF",
                                        },
                                        display: 'table-row',
                                        verticalAlign: 'bottom',
                                    }}
                                    rowsPerPageOptions={[25, 50, 100]}
                                    component='Box'
                                    count={1000}
                                    rowsPerPage={pagination.pageSize}
                                    page={pagination.pageNumber - 1}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                    labelRowsPerPage={'Filas por pagina'}
                                    labelDisplayedRows={({ from, to, count }) =>
                                        `${from}–${to} de ${count !== -1 ? count : `más de ${to}`}`
                                    }
                                />
                            </Paper>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </React.Fragment >
    )
}